/**
 * HOPE Templates & iQIES Scheduling Components
 * 
 * Provides:
 * - Template selection for common clinical scenarios
 * - Pre-filled symptom management protocols
 * - iQIES submission scheduling UI
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { ScrollArea } from "./ui/scroll-area";
import { Separator } from "./ui/separator";
import { Switch } from "./ui/switch";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Heart,
  Stethoscope,
  Brain,
  Wind,
  Activity,
  Clock,
  Calendar,
  FileText,
  AlertTriangle,
  CheckCircle,
  Play,
  Pause,
  Trash2,
  RefreshCw,
  Download,
  Send,
  Settings,
  ClipboardList,
  ChevronRight,
  Plus,
  Zap,
  ListChecks,
  Timer,
  Bell
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";
const TEMPLATES_API = `${API_URL}/api/hope-templates`;

// Category icons mapping
const CATEGORY_CONFIG = {
  pain_management: { icon: Activity, color: "text-red-400", bg: "bg-red-500/10" },
  respiratory: { icon: Wind, color: "text-blue-400", bg: "bg-blue-500/10" },
  cardiac: { icon: Heart, color: "text-pink-400", bg: "bg-pink-500/10" },
  oncology: { icon: Zap, color: "text-purple-400", bg: "bg-purple-500/10" },
  dementia: { icon: Brain, color: "text-amber-400", bg: "bg-amber-500/10" },
  comfort_care: { icon: Stethoscope, color: "text-teal-400", bg: "bg-teal-500/10" },
  end_of_life: { icon: Clock, color: "text-slate-400", bg: "bg-slate-500/10" },
  general: { icon: ClipboardList, color: "text-green-400", bg: "bg-green-500/10" }
};

// Template Card Component
function TemplateCard({ template, onSelect, selected }) {
  const config = CATEGORY_CONFIG[template.category] || CATEGORY_CONFIG.general;
  const Icon = config.icon;

  return (
    <Card 
      className={`cursor-pointer transition-all hover:border-slate-600 ${
        selected ? "border-teal-500 bg-teal-500/5" : "bg-slate-800/50 border-slate-700"
      }`}
      onClick={() => onSelect(template)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className={`p-2 rounded-lg ${config.bg}`}>
            <Icon className={`w-5 h-5 ${config.color}`} />
          </div>
          {template.sfv_triggers?.length > 0 && (
            <Badge className="bg-amber-500/20 text-amber-300 text-xs">
              SFV Auto-Trigger
            </Badge>
          )}
        </div>
        <div className="mt-3">
          <h4 className="font-medium text-sm">{template.name}</h4>
          <p className="text-xs text-slate-400 mt-1">{template.description}</p>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <Timer className="w-3 h-3" />
            {template.estimated_time}
          </span>
          <Badge variant="outline" className="capitalize text-xs">
            {template.category?.replace(/_/g, " ")}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}

// HOPE Templates Selection Component
export function HOPETemplateSelector({ patientId, patientName, onApply, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [groupedTemplates, setGroupedTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [applying, setApplying] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${TEMPLATES_API}/templates`);
      setTemplates(response.data.templates || []);
      setGroupedTemplates(response.data.grouped || {});
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;

    setApplying(true);
    try {
      const response = await axios.post(`${TEMPLATES_API}/templates/apply`, {
        template_id: selectedTemplate.id,
        patient_id: patientId
      });

      if (response.data.success) {
        toast.success(`Template "${selectedTemplate.name}" applied`, {
          description: response.data.sfv_required 
            ? "SFV required within 48 hours" 
            : "Draft created successfully"
        });

        // Show care plan suggestions
        if (response.data.template?.care_plan_suggestions?.length > 0) {
          toast.info("Care Plan Suggestions", {
            description: response.data.template.care_plan_suggestions[0],
            duration: 5000
          });
        }

        if (onApply) onApply(response.data.record);
      }
    } catch (err) {
      toast.error("Failed to apply template");
      console.error(err);
    } finally {
      setApplying(false);
    }
  };

  const filteredTemplates = activeCategory === "all" 
    ? templates 
    : templates.filter(t => t.category === activeCategory);

  const categories = [
    { id: "all", name: "All Templates" },
    { id: "pain_management", name: "Pain Management" },
    { id: "respiratory", name: "Respiratory" },
    { id: "cardiac", name: "Cardiac" },
    { id: "oncology", name: "Oncology" },
    { id: "dementia", name: "Dementia" },
    { id: "end_of_life", name: "End of Life" },
    { id: "general", name: "General" }
  ];

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ListChecks className="w-5 h-5 text-teal-400" />
          <div>
            <h3 className="font-semibold">HOPE Form Templates</h3>
            <p className="text-xs text-slate-400">Pre-filled protocols for common clinical scenarios</p>
          </div>
        </div>
        {patientName && (
          <Badge variant="outline">{patientName}</Badge>
        )}
      </div>

      {/* Category Filter */}
      <ScrollArea className="w-full">
        <div className="flex gap-2 pb-2">
          {categories.map(cat => (
            <Button
              key={cat.id}
              variant={activeCategory === cat.id ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveCategory(cat.id)}
              className="whitespace-nowrap"
            >
              {cat.name}
            </Button>
          ))}
        </div>
      </ScrollArea>

      {/* Templates Grid */}
      <ScrollArea className="h-[400px]">
        {loading ? (
          <div className="text-center py-8 text-slate-400">Loading templates...</div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredTemplates.map(template => (
              <TemplateCard
                key={template.id}
                template={template}
                selected={selectedTemplate?.id === template.id}
                onSelect={setSelectedTemplate}
              />
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Selected Template Details */}
      {selectedTemplate && (
        <Card className="bg-slate-800 border-teal-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span>{selectedTemplate.name}</span>
              <Badge className="bg-teal-500/20 text-teal-300">Selected</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {selectedTemplate.sfv_triggers?.length > 0 && (
              <Alert className="bg-amber-500/10 border-amber-500/30">
                <AlertTriangle className="h-4 w-4 text-amber-400" />
                <AlertDescription className="text-amber-200 text-xs">
                  This template triggers SFV for: {selectedTemplate.sfv_triggers.join(", ")}
                </AlertDescription>
              </Alert>
            )}
            
            {selectedTemplate.care_plan_suggestions?.length > 0 && (
              <div>
                <Label className="text-xs text-slate-400">Care Plan Suggestions</Label>
                <ul className="mt-1 space-y-1">
                  {selectedTemplate.care_plan_suggestions.slice(0, 3).map((suggestion, idx) => (
                    <li key={idx} className="text-xs text-slate-300 flex items-start gap-2">
                      <ChevronRight className="w-3 h-3 mt-0.5 text-teal-400 flex-shrink-0" />
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-700">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleApplyTemplate} 
          disabled={!selectedTemplate || applying}
          className="bg-teal-600 hover:bg-teal-700"
        >
          {applying ? "Applying..." : "Apply Template"}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

// iQIES Schedule Card
function ScheduleCard({ schedule, onRun, onEdit, onDelete }) {
  const isActive = schedule.status === "scheduled";
  const isFailed = schedule.status === "failed";

  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isActive ? "bg-green-500/10" : isFailed ? "bg-red-500/10" : "bg-slate-500/10"}`}>
              <Calendar className={`w-4 h-4 ${isActive ? "text-green-400" : isFailed ? "text-red-400" : "text-slate-400"}`} />
            </div>
            <div>
              <h4 className="font-medium text-sm">{schedule.name}</h4>
              <p className="text-xs text-slate-400 capitalize">{schedule.frequency} at {schedule.run_time}</p>
            </div>
          </div>
          <Badge 
            className={
              isActive ? "bg-green-500/20 text-green-300" :
              isFailed ? "bg-red-500/20 text-red-300" :
              "bg-slate-500/20 text-slate-300"
            }
          >
            {schedule.status}
          </Badge>
        </div>

        <Separator className="my-3" />

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-slate-500">Next Run:</span>
            <p className="text-slate-300">
              {new Date(schedule.next_run).toLocaleString()}
            </p>
          </div>
          {schedule.last_run && (
            <div>
              <span className="text-slate-500">Last Run:</span>
              <p className="text-slate-300">
                {new Date(schedule.last_run).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        {schedule.last_result && (
          <div className="mt-2 p-2 rounded bg-slate-900 text-xs">
            {schedule.last_result.success ? (
              <span className="text-green-400">
                ✓ {schedule.last_result.record_count} records exported
              </span>
            ) : (
              <span className="text-red-400">
                ✗ {schedule.last_result.error || `${schedule.last_result.validation_errors?.length || 0} validation errors`}
              </span>
            )}
          </div>
        )}

        <div className="mt-3 flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => onRun(schedule.schedule_id)}>
            <Play className="w-3 h-3 mr-1" />
            Run Now
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onEdit(schedule)}>
            <Settings className="w-3 h-3" />
          </Button>
          <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => onDelete(schedule.schedule_id)}>
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// iQIES Scheduling Manager
export function IQIESScheduleManager({ onClose }) {
  const [schedules, setSchedules] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    name: "",
    frequency: "weekly",
    run_time: "02:00",
    auto_lock: true,
    notification_emails: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, submissionsRes] = await Promise.all([
        axios.get(`${TEMPLATES_API}/iqies/schedules`),
        axios.get(`${TEMPLATES_API}/iqies/submissions?limit=10`)
      ]);
      setSchedules(schedulesRes.data.schedules || []);
      setSubmissions(submissionsRes.data.submissions || []);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchedule = async () => {
    try {
      const response = await axios.post(`${TEMPLATES_API}/iqies/schedules`, newSchedule);
      if (response.data.success) {
        toast.success("Schedule created successfully");
        setShowCreateDialog(false);
        fetchData();
        setNewSchedule({
          name: "",
          frequency: "weekly",
          run_time: "02:00",
          auto_lock: true,
          notification_emails: []
        });
      }
    } catch (err) {
      toast.error("Failed to create schedule");
    }
  };

  const handleRunNow = async (scheduleId) => {
    try {
      await axios.post(`${TEMPLATES_API}/iqies/schedules/${scheduleId}/run`);
      toast.success("iQIES submission started");
      setTimeout(fetchData, 2000);
    } catch (err) {
      toast.error("Failed to start submission");
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await axios.delete(`${TEMPLATES_API}/iqies/schedules/${scheduleId}`);
      toast.success("Schedule deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete schedule");
    }
  };

  const handleDownloadXml = async (submissionId) => {
    try {
      const response = await axios.get(
        `${TEMPLATES_API}/iqies/submissions/${submissionId}/download`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hope_batch_${submissionId}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error("Failed to download XML");
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Send className="w-5 h-5 text-teal-400" />
          <div>
            <h3 className="font-semibold">iQIES Submission Scheduling</h3>
            <p className="text-xs text-slate-400">Automated CMS batch exports</p>
          </div>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} size="sm">
          <Plus className="w-4 h-4 mr-1" />
          New Schedule
        </Button>
      </div>

      <Tabs defaultValue="schedules">
        <TabsList className="grid grid-cols-2 w-full bg-slate-800">
          <TabsTrigger value="schedules">Schedules</TabsTrigger>
          <TabsTrigger value="history">Submission History</TabsTrigger>
        </TabsList>

        <TabsContent value="schedules" className="space-y-3">
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading...</div>
          ) : schedules.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-8 text-center">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-400">No schedules configured</p>
                <p className="text-xs text-slate-500 mt-1">Create a schedule to automate iQIES submissions</p>
              </CardContent>
            </Card>
          ) : (
            schedules.map(schedule => (
              <ScheduleCard
                key={schedule.schedule_id}
                schedule={schedule}
                onRun={handleRunNow}
                onEdit={() => {}}
                onDelete={handleDeleteSchedule}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-3">
          {submissions.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-8 text-center">
                <FileText className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                <p className="text-slate-400">No submissions yet</p>
              </CardContent>
            </Card>
          ) : (
            submissions.map(submission => (
              <Card key={submission.id} className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">
                        Batch #{submission.id.slice(-6)}
                      </p>
                      <p className="text-xs text-slate-400">
                        {new Date(submission.submitted_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-green-500/20 text-green-300">
                        {submission.record_count} records
                      </Badge>
                      <Button size="sm" variant="ghost" onClick={() => handleDownloadXml(submission.id)}>
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Create Schedule Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle>Create iQIES Schedule</DialogTitle>
            <DialogDescription>
              Configure automatic submission of locked HOPE records
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Schedule Name</Label>
              <Input
                value={newSchedule.name}
                onChange={(e) => setNewSchedule({...newSchedule, name: e.target.value})}
                placeholder="e.g., Weekly CMS Submission"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select 
                  value={newSchedule.frequency}
                  onValueChange={(v) => setNewSchedule({...newSchedule, frequency: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Run Time</Label>
                <Input
                  type="time"
                  value={newSchedule.run_time}
                  onChange={(e) => setNewSchedule({...newSchedule, run_time: e.target.value})}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-Lock Completed Records</Label>
                <p className="text-xs text-slate-400">Automatically lock completed records before export</p>
              </div>
              <Switch
                checked={newSchedule.auto_lock}
                onCheckedChange={(c) => setNewSchedule({...newSchedule, auto_lock: c})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateSchedule} disabled={!newSchedule.name}>
              Create Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="pt-4 border-t border-slate-700">
        <Button variant="ghost" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}

// Quick Template Button for HOS Page
export function QuickTemplateButton({ patientId, patientName, onApply }) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <ListChecks className="w-4 h-4" />
          Templates
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
        <HOPETemplateSelector
          patientId={patientId}
          patientName={patientName}
          onApply={(record) => {
            setOpen(false);
            if (onApply) onApply(record);
          }}
          onClose={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

export default HOPETemplateSelector;
